import { pipe } from "fp-ts/lib/pipeable";
import { An, Ar, O, Ord } from "../prelude";
export function fromNonEmptyArray<A>(as: An.NonEmptyArray<A>): Array<A> {
  return as;
}

export function unsafeFromArray<A>(as: Array<A>) {
  return as as unknown as An.NonEmptyArray<A>;
}

function minNumber<A>(as: An.NonEmptyArray<A>, acc: (a: A) => number) {
  const ordA = Ord.contramap((a: A) => acc(a))(Ord.ordNumber);
  return acc(An.min(ordA)(as));
}

function maxNumber<A>(as: An.NonEmptyArray<A>, acc: (a: A) => number) {
  const ordA = Ord.contramap((a: A) => acc(a))(Ord.ordNumber);
  return acc(An.max(ordA)(as));
}

export function extentNumber<A>(as: An.NonEmptyArray<A>, acc: (a: A) => number) {
  return [minNumber(as, acc), maxNumber(as, acc)];
}

export function insertionOrderSet<A>(data: An.NonEmptyArray<A>, accessor: (a: A) => string): An.NonEmptyArray<string> {
  return Array.from(new Set(data.map(accessor)).values()) as An.NonEmptyArray<string>;
}

/**
 * invertSparseArray
 * Used to create the "inverse" of a sparse array for rendering lines where there
 * aren't any in a line chart.
 *
 * Very specific to line charts at the moment, also with the "value" field requirement,
 * but could be made more generic if useful.
 *
 * NOTE: If this turns out to be too performance intensive (due to the heavy use
 * of Option), we could consider using flatMap – which I tried, but the compiler
 * didn't accept it for some reason.
 *
 * Normal line:  –   ––– – –––
 * Inverse line: ----- -----
 *
 * Example:
 *
 *   const input = [
 *     { value: 1 },
 *     { value: null },
 *     { value: null },
 *     { value: null },
 *     { value: 2 },
 *     { value: 3 },
 *     { value: 4 },
 *     { value: null },
 *     { value: 5 },
 *     { value: null },
 *     { value: 6 },
 *     { value: 7 },
 *     { value: 8 }
 *   ];
 *
 *   const output = [
 *     { value: 1 },
 *     { value: 2 },
 *     { value: null },
 *     { value: 4 },
 *     { value: 5 },
 *     { value: 6 },
 *     { value: null }
 *   ];
 */
export function invertSparseArray<A extends { value: number | null }>(xs: Array<A>) {
  const defined = (x: A) => x.value != null;
  return pipe(
    xs,
    Ar.filterMapWithIndex((i, curr) => {
      const hasPrev = pipe(Ar.lookup(i - 1, xs), O.exists(defined));
      const hasCurr = defined(curr);
      const hasNext = pipe(Ar.lookup(i + 1, xs), O.exists(defined));
      const isLast = i === xs.length - 1;

      return isLast
        ? !hasPrev && hasCurr
          ? O.some(curr)
          : O.none
        : !hasPrev && hasCurr
          ? O.some(curr)
          : hasPrev && hasCurr && !hasNext
            ? O.some(curr)
            : hasPrev && hasCurr && hasNext
              ? O.some({ ...curr, value: null })
              : O.none;
    }),
  );
}
